import { API_ENDPOINTS } from "../constants/api";
import { handleAPIErrorBasedOnRcode } from "../utils/UtilsRcode";
import {
	getCachedMaintenanceState,
	isMaintenanceConfigLoaded,
} from "../hooks/maintenance-page-hooks";
import { summaryDefaults } from "../data/defaults";
import { RESTRICTED } from "../constants/storage";
import { store } from "../store";
import { actionResetAuth } from "../store/actions/auth-actions";
import { actionLogout } from "../store/actions/user-actions";
import { clearAuthStorage } from "./auth-storage";

/** Maintenance config path - served from public directory */
const MAINTENANCE_CONFIG_PATH = "/maintenance-page/state.json";

/**
 * Handle 401 Unauthorized responses by clearing frontend auth state.
 * Called when backend session is invalid or expired.
 */
const handle401 = () => {
	store.dispatch(actionLogout());
	store.dispatch(actionResetAuth());
	clearAuthStorage();
};

const Http = {
	getHeaders: async (extraHeaders?: any) => {
		const defaultHeaders = {
			Pragma: "no-cache",
			"Cache-Control": "no-cache",
			"x-sc-useragent": ";;WebPortal=0.1;Windows NT 6.1; WOW64;",
			"Content-type": "application/json;charset=UTF-8",
			"x-sc-client-os-version": 27,
		};

		return extraHeaders
			? { ...defaultHeaders, ...extraHeaders }
			: defaultHeaders;
	},

	/**
	 * Checks if the API call should be aborted based on maintenance mode and user restrictions
	 * @param url The API endpoint URL
	 * @returns true if the call should be aborted, false otherwise
	 */
	shouldAbortApiCall: (url: string): boolean => {
		try {
			// Check if maintenance config has been loaded yet
			const isConfigLoaded = isMaintenanceConfigLoaded();

			if (!isConfigLoaded) {
				// Block all API calls except the maintenance config endpoint until it's
				// loaded. PROFILE is exempt alongside GEOIP so a login can start
				// resolving immediately instead of queueing behind the maintenance and
				// site-settings round trips; the cost is one wasted call on the rare
				// load where maintenance turns out to be enabled.
				if (
					!url.includes(MAINTENANCE_CONFIG_PATH) &&
					!url.includes(API_ENDPOINTS.GEOIP) &&
					!url.includes(API_ENDPOINTS.PROFILE) &&
					!url.includes(API_ENDPOINTS.ADSSO_LOGIN_VERIFY)
				) {
					console.warn("API call blocked: Maintenance mode is on", url);
					return true;
				}
			}

			// Check if maintenance mode is enabled by accessing the cached state from useMaintenance hook
			const cachedState = getCachedMaintenanceState();
			const maintenanceEnabled = cachedState?.enabled || false;

			if (maintenanceEnabled) {
				// Allow maintenance config endpoint to proceed. The ADSSO verify call
				// is exempt too: a blocked call reads as "not authenticated", which
				// would bounce the user to SSO and back into the same blocked call.
				if (
					!url.includes(MAINTENANCE_CONFIG_PATH) &&
					!url.includes(API_ENDPOINTS.ADSSO_LOGIN_VERIFY)
				) {
					console.warn("API call blocked during maintenance:", url);
					return true;
				}
			}

			// Check if user is restricted
			const isUserRestricted = sessionStorage.getItem(RESTRICTED) === "true";

			if (isUserRestricted) {
				// Check if URL contains restricted paths
				const restrictedPaths = ["/summary", "/service"];
				const hasRestrictedPath = restrictedPaths.some((path) =>
					url.includes(path)
				);

				return hasRestrictedPath;
			}

			return false;
		} catch (error) {
			console.error("Error checking API call restrictions:", error);
			// If there's an error checking restrictions, allow the call to proceed
			return false;
		}
	},

	get: async (url: string, extraHeaders?: any, isPublic?: false) => {
		// Check if API call should be aborted due to maintenance or user restrictions
		if (Http.shouldAbortApiCall(url)) {
			// Return empty response as mentioned in the existing comment
			if (url.includes(API_ENDPOINTS.SUMMARY)) return summaryDefaults;
			return {};
		}

		const headers = await Http.getHeaders(extraHeaders);
		// add debug for loki logs
		const response = await fetch(url, {
			method: "GET",
			headers: { ...headers },
		});

		// Handle 401 Unauthorized (session expired/invalid)
		if (response.status === 401) {
			handle401();
			throw new Error("Session expired");
		}

		const responseData = await response.json();

		return responseData;
	},

	post: async (
		url: string,
		data?: any,
		extraHeaders?: any,
		controller?: any
	) => {
		try {
			if (Http.shouldAbortApiCall(url)) {
				return {};
			}
			const headers = await Http.getHeaders(extraHeaders);
			// add debug for loki logs
			const response = await fetch(url, {
				method: "POST",
				headers,
				body: JSON.stringify(data),
				...controller,
			});

			// The ADSSO site gate is separate from the user's login session: its
			// failures are answered by an SSO redirect, not by an error page or by
			// tearing down the SA session.
			const isAdssoVerify = url.includes(API_ENDPOINTS.ADSSO_LOGIN_VERIFY);

			// Handle 401 Unauthorized (session expired/invalid)
			if (response.status === 401 && !isAdssoVerify) {
				handle401();
				throw new Error("Session expired");
			}

			const responseData = await response.json();

			// handle http errors
			if (!response.ok && !responseData.rcode) {
				const errorText = await response.text(); // await to read the response text
				throw new Error(`HTTP: ${response.status} - ${errorText}`);
			}

			// handle rcodes error. An expired ADSSO token answers 403/4030000 —
			// that's the caller's cue to re-authenticate, not an error page.
			if (responseData.rcode > 0) {
				if (!isAdssoVerify) {
					handleAPIErrorBasedOnRcode(responseData.rcode);
				}
				// Throw for both ADSSO and non-ADSSO so verifyAdssoToken can catch
				// the error and return false to trigger a redirect to SSO
				return Promise.reject(responseData);
			}

			return responseData;
		} catch (e: any) {
			console.error(e);
			throw e;
		}
	},
};

export default Http;
